// Copyright (c) 2022 by Move Closer

import { ComponentInternalInstance, computed, toRefs } from '@vue/composition-api'

import {
  canBeDisabledProp,
  doubleClickProps,
  hasSizeProp, hasVariantProp,
  useDoubleClick,
  useSizeClass,
  useThemeClass, useVariantClass
} from '../../composables'
import { ComponentObjectPropsOptions } from '../../vue-api'

import { ButtonProps, Theme } from './Button.contract'
import { buttonVariantRegistry, sizeClassRegistry, themeClassRegistry } from './Button.config'

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl> (original)
 * @author Wojciech Falkowski <wojciech.falkowski@movecloser.pl> (edited)
 */
export const buttonProps: ComponentObjectPropsOptions<ButtonProps> = {
  ...hasVariantProp,
  ...canBeDisabledProp,
  ...doubleClickProps,
  ...hasSizeProp,

  /**
   * @see Button.confirmationText
   */
  confirmationLabel: {
    type: String,
    required: false
  },

  /**
   * @see Button.hasLabel
   */
  hideLabelOnMobile: {
    type: Boolean,
    required: false,
    default: false
  },

  /**
   * @see Button.isFluid
   */
  isFluid: {
    type: Boolean,
    required: false,
    default: false
  },

  /**
   * @see Button.isLoading
   */
  isLoading: {
    type: Boolean,
    required: false,
    default: false
  },

  /**
   * @see Button.label
   */
  label: {
    type: String,
    required: false,
    default: ''
  },

  /**
   * Button's theme (style variant).
   */
  theme: {
    type: String,
    required: false,
    default: Theme.Primary
  }
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl> (original)
 * @author Wojciech Falkowski <wojciech.falkowski@movecloser.pl> (edited)
 */
export function useButton (props: ButtonProps, internalInstance: ComponentInternalInstance | null) {
  if (internalInstance === null) {
    throw new Error('useLink(): FATAL ERROR! Failed to resolve the component instance!')
  }

  const { doubleClick, size, theme, variant } = toRefs(props)
  const { proxy, type } = internalInstance

  const _emitClick = (e?: Event): void => {
    internalInstance.emit('click', e)
  }

  const { handleClick, isPendingClick } = useDoubleClick(
    _emitClick,
    doubleClick,
    props.clickPeriod
  )

  const message = computed<string>(() => {
    const confirmation: string = props.confirmationLabel || proxy.$t('dsl.button.confirmation').toString()
    return isPendingClick.value ? confirmation : props.label
  })

  const { sizeClass } = useSizeClass<string>(size, sizeClassRegistry, `${type._componentTag}`)
  const { themeClass } = useThemeClass<string>(theme, themeClassRegistry, `${type.componentTag}`)
  const { variantClass } = useVariantClass<string>(variant, buttonVariantRegistry, `${type.componentTag}`)

  return { handleClick, isPendingClick, message, sizeClass, themeClass, variantClass }
}
